import React, { useState, useEffect } from 'react';
import { Row, Col, Form, Button, ListGroup } from 'react-bootstrap';
import { ExtraButton, ActionButton, ShareButton } from '../types';
import { getAcademyDropdown } from '../../../api/academy/academyService';
import { getTournamentDropdown } from '../../../api/tournament/tournamentService';
import { getWeeklyReportDropdown } from '../../../api/weeklyReport/weeklyReportService';
import { getWeeklyReport } from '../../../api/weeklyReport/getWeeklyReport';
import { getTournamentSeasons, getAllTournamentSeasons, TournamentSeason } from '../../../api/tournament/manageTournamentService';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { SketchPicker, ColorResult } from 'react-color';

const defaultShareButton: ShareButton = {
  category: 'share',
  color: '#00BA5C',
  textColor: '#FFFFFF',
  label: 'Share',
  icon: 'share',
  iconPosition: 'right',
  redirection: {
    type: 'share',
    path: '',
  },
  desc:
    'Hey! Just came across on the SFL – Sports For Life app.\n\nThey offer quality football coaching for young players looking to take their game to the next level. ⚽\n\nCheck it out on the SFL app – download now!\n👉 www.sportsforlife.in/app-invite',
  image: '',
};

const defaultActionButton: ActionButton = {
  category: 'button',
  color: '#0077F9',
  textColor: '#FFFFFF',
  label: 'Explore Now',
  icon: 'arrow_right',
  iconPosition: 'right',
  desc: '',
  image: '',
  redirectionType: 'URL',
};

interface ExtraButtonsFormProps {
  buttons: ExtraButton[];
  onChange: (buttons: ExtraButton[]) => void;
  platform?: string;
}

const categoryOptions = [
  { value: 'button', label: 'Button' },
  { value: 'share', label: 'Share' },
];
const iconPositionOptions = [
  { value: 'left', label: 'Left' },
  { value: 'right', label: 'Right' },
];
const redirectionTypeOptions = [
  { value: 'URL', label: 'URL' },
  { value: 'Page', label: 'Page' },
  { value: 'Custom', label: 'Custom' },
  { value: 'Academy', label: 'Academy' },
  { value: 'AcademyDetailsScreen', label: 'Academy Details' },
  { value: 'TournamentScreen', label: 'Tournament Screen' },
  { value: 'TournamentTab', label: 'Tournament Tab' },
  { value: 'WeeklyReport', label: 'Weekly Report' },
];

const tabNameOptions = [
  { value: 'Home', label: 'Home' },
  { value: 'Fixtures', label: 'Fixtures' },
  { value: 'Standings', label: 'Standings' },
  { value: 'Player Stats', label: 'Player Stats' },
  { value: 'Highlights', label: 'Highlights' },
  { value: 'Voting', label: 'Voting' },
  { value: 'Awards', label: 'Awards' },
  { value: 'My Stats', label: 'My Stats' },
];

const ColorPickerInput: React.FC<{ color: string; onChange: (color: string) => void }> = ({ color, onChange }) => {
  const [displayColorPicker, setDisplayColorPicker] = useState(false);

  return (
    <div>
      <div onClick={() => setDisplayColorPicker(!displayColorPicker)} style={{ cursor: 'pointer', width: '36px', height: '36px', borderRadius: '2px', background: color, border: '1px solid #ccc' }} />
      {displayColorPicker ? (
        <div style={{ position: 'absolute', zIndex: 2 }}>
          <div style={{ position: 'fixed', top: '0px', right: '0px', bottom: '0px', left: '0px' }} onClick={() => setDisplayColorPicker(false)} />
          <SketchPicker color={color} onChange={(c: ColorResult) => onChange(c.hex)} />
        </div>
      ) : null}
    </div>
  );
};

const ActionButtonForm: React.FC<{ button: ActionButton; onChange: (button: ActionButton) => void; platform?: string }> = ({ button, onChange, platform }) => {
  const [academyOptions, setAcademyOptions] = useState<any[]>([]);
  const [tournamentOptions, setTournamentOptions] = useState<any[]>([]);
  const [weeklyReportOptions, setWeeklyReportOptions] = useState<any[]>([]);
  const [seasonOptions, setSeasonOptions] = useState<any[]>([]);
  const [seasonsLoading, setSeasonsLoading] = useState(false);
  const [defaultSeason, setDefaultSeason] = useState<TournamentSeason | null>(null);
  const [hasSeasonSeasons, setHasSeasonSeasons] = useState(false);

  useEffect(() => {
    if (platform === 'web' && button.redirectionType !== 'URL') {
      onChange({ ...button, redirectionType: 'URL' });
    }
  }, [platform, button.redirectionType, onChange]);

  const filteredRedirectionTypeOptions = platform === 'web'
    ? redirectionTypeOptions.filter(opt => opt.value === 'URL')
    : redirectionTypeOptions;

  const loadAcademies = async (inputValue: string) => {
    const data = await getAcademyDropdown();
    const options = data.map((acc: any) => ({
      value: acc._id,
      label: `${acc.academyInfo.basicDetails.academyName} (${acc.basicDetails.location})`,
      academyId: acc.academyInfo._id,
      academyName: acc.academyInfo.basicDetails.academyName,
      coachingCenterId: acc._id,
      academyLocation: acc.basicDetails.location,
    }));
    return options.filter((i: any) => i.label.toLowerCase().includes(inputValue.toLowerCase()));
  };

  const loadTournaments = async (inputValue: string) => {
    const data = await getTournamentDropdown();
    const options = data.map((t: any) => ({
      value: t._id,
      label: t.name,
      sports: t.sports,
    }));
    console.log('Tournament options:', options);
    return options.filter((i: any) => i.label.toLowerCase().includes(inputValue.toLowerCase()));
  };

  const loadWeeklyReports = async (inputValue: string) => {
    const data = await getWeeklyReportDropdown();
    const options = data.map((r: any) => ({
      value: r._id,
      label: r.title,
    }));
    return options.filter((i: any) => i.label.toLowerCase().includes(inputValue.toLowerCase()));
  };

  useEffect(() => {
    // Dynamically fetch weekly report title if missing
    if (
      button.redirectionType === 'WeeklyReport' &&
      button.weeklyReportId &&
      !button.weeklyReportName
    ) {
      getWeeklyReport(button.weeklyReportId).then((res) => {
        // Try to get the title from common response shapes
        const title = res?.data?.title || res?.title || '';
        if (title) {
          onChange({ ...button, weeklyReportName: title });
        }
      });
    }
  }, [button.redirectionType, button.weeklyReportId, button.weeklyReportName]);

  // Load seasons when tournament is selected
  useEffect(() => {
    const loadSeasons = async () => {
      if (
        (button.redirectionType === 'TournamentScreen' || button.redirectionType === 'TournamentTab') &&
        button.tournamentId
      ) {
        setSeasonsLoading(true);
        try {
          // Fetch all seasons including DEFAULT
          const allSeasons = await getAllTournamentSeasons(button.tournamentId, button.sports);
          
          // Find DEFAULT season
          const defaultSeasonFound = allSeasons.find((s: TournamentSeason) => s.seasonType === 'DEFAULT');
          setDefaultSeason(defaultSeasonFound || null);
          
          // Get SEASON type seasons
          const seasonTypeSeasons = allSeasons.filter((s: TournamentSeason) => s.seasonType === 'SEASON');
          setHasSeasonSeasons(seasonTypeSeasons.length > 0);
          
          // Create options from SEASON type seasons
          const options = seasonTypeSeasons.map((season: TournamentSeason) => ({
            value: season._id,
            label: season.seasonLabel || season.seasonYearRange || `Season ${season.seasonNumber}`,
            seasonName: season.seasonLabel || season.seasonYearRange || `Season ${season.seasonNumber}`,
            seasonType: season.seasonType,
          }));
          setSeasonOptions(options);
          
          // If no SEASON seasons exist but DEFAULT exists, auto-select DEFAULT
          if (seasonTypeSeasons.length === 0 && defaultSeasonFound && !button.tournamentSeason) {
            onChange({
              ...button,
              tournamentSeason: defaultSeasonFound._id,
              seasonName: undefined, // Don't set name for DEFAULT season
            });
          }
          
          // If editing and saved season is DEFAULT, clear seasonName
          if (button.tournamentSeason && defaultSeasonFound && button.tournamentSeason === defaultSeasonFound._id) {
            if (button.seasonName) {
              onChange({
                ...button,
                seasonName: undefined, // Clear name for DEFAULT season
              });
            }
          }
          
          // If editing and saved season is a SEASON type, ensure seasonName is set
          if (button.tournamentSeason && seasonTypeSeasons.length > 0) {
            const savedSeason = seasonTypeSeasons.find((s: TournamentSeason) => s._id === button.tournamentSeason);
            if (savedSeason && !button.seasonName) {
              // Set seasonName if it's missing for SEASON type
              onChange({
                ...button,
                seasonName: savedSeason.seasonLabel || savedSeason.seasonYearRange || `Season ${savedSeason.seasonNumber}`,
              });
            }
          }
        } catch (error) {
          console.error('Error loading seasons:', error);
          setSeasonOptions([]);
          setDefaultSeason(null);
          setHasSeasonSeasons(false);
        } finally {
          setSeasonsLoading(false);
        }
      } else {
        setSeasonOptions([]);
        setDefaultSeason(null);
        setHasSeasonSeasons(false);
      }
    };

    loadSeasons();
  }, [button.redirectionType, button.tournamentId]);

  // Clear season when tournament is cleared
  useEffect(() => {
    if (
      (button.redirectionType === 'TournamentScreen' || button.redirectionType === 'TournamentTab') &&
      !button.tournamentId &&
      button.tournamentSeason
    ) {
      onChange({ ...button, tournamentSeason: undefined, seasonName: undefined });
    }
  }, [button.tournamentId, button.redirectionType]);

  return (
    <>
      <Form.Group className="mb-3">
        <Form.Label>Redirection Type</Form.Label>
        <Select
          options={filteredRedirectionTypeOptions}
          value={filteredRedirectionTypeOptions.find(opt => opt.value === button.redirectionType) || null}
          onChange={option => {
            const newType = option ? (option.value as ActionButton['redirectionType']) : 'URL';
            onChange({ ...button, redirectionType: newType });
          }}
          isDisabled={platform === 'web'}
        />
      </Form.Group>
      
      {button.redirectionType === 'URL' && (
        <Form.Group className="mb-3">
          <Form.Label>URL</Form.Label>
          <Form.Control
            type="text"
            value={button.url || ''}
            onChange={(e) => onChange({ ...button, url: e.target.value })}
            placeholder="Enter URL (e.g., https://example.com)"
          />
        </Form.Group>
      )}

      {button.redirectionType === 'Page' && (
        <Form.Group className="mb-3">
          <Form.Label>Page</Form.Label>
          <Form.Control
            type="text"
            value={button.page || ''}
            onChange={(e) => onChange({ ...button, page: e.target.value })}
            placeholder="Enter page path"
          />
        </Form.Group>
      )}

      {button.redirectionType === 'Custom' && (
        <>
          <Form.Group className="mb-3">
            <Form.Label>Type</Form.Label>
            <Form.Control
              type="text"
              value={button.customType || ''}
              onChange={(e) => onChange({ ...button, customType: e.target.value })}
              placeholder="Enter custom type"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Path</Form.Label>
            <Form.Control
              type="text"
              value={button.customPath || ''}
              onChange={(e) => onChange({ ...button, customPath: e.target.value })}
              placeholder="Enter custom path"
            />
          </Form.Group>
        </>
      )}

      {button.redirectionType === 'AcademyDetailsScreen' && (
        <Form.Group className="mb-3">
          <Form.Label>Select Academy</Form.Label>
          <AsyncSelect
            cacheOptions
            defaultOptions
            loadOptions={async (inputValue: string) => {
              const options = await loadAcademies(inputValue);
              setAcademyOptions(options);
              return options;
            }}
            value={
              button.academyId
                ? (() => {
                    const found = academyOptions.find(opt => opt.value === button.academyId);
                    if (found) return found;
                    // fallback: use academyName and location if available
                    let location = button.academyLocation || button.location || '';
                    // try to find location from loaded options if not present
                    if (!location && found) location = found.academyLocation || '';
                    return {
                      value: button.academyId,
                      label: `${button.academyName}${location ? ` (${location})` : ''}`,
                      academyId: button.academyId,
                      academyName: button.academyName,
                      coachingCenterId: button.coachingCenterId,
                      academyLocation: location,
                    };
                  })()
                : null
            }
            onChange={(option: any) => onChange({ ...button, academyId: option?.academyId || '', academyName: option?.academyName || '', coachingCenterId: option?.coachingCenterId || '', academyLocation: option?.academyLocation || option?.location || '' })}
          />
        </Form.Group>
      )}

      {(button.redirectionType === 'TournamentScreen' || button.redirectionType === 'TournamentTab') && (
        <Form.Group className="mb-3">
          <Form.Label>Select Tournament</Form.Label>
          <AsyncSelect
            cacheOptions
            defaultOptions
            loadOptions={async (inputValue: string) => {
              const options = await loadTournaments(inputValue);
              setTournamentOptions(options);
              return options;
            }}
            value={
              button.tournamentId
                ? tournamentOptions.find(opt => opt.value === button.tournamentId) ||
                  (button.tournamentName
                    ? { value: button.tournamentId, label: button.tournamentName, sports: button.sports }
                    : null)
                : null
            }
            onChange={(option: any) => {
              // Clear season when tournament changes
              onChange({ 
                ...button, 
                tournamentId: option?.value, 
                tournamentName: option?.label, 
                sports: option?.sports,
                tournamentSeason: undefined,
                seasonName: undefined
              });
            }}
          />
        </Form.Group>
      )}

      {(button.redirectionType === 'TournamentScreen' || button.redirectionType === 'TournamentTab') && button.tournamentId && (
        <Form.Group className="mb-3">
          <Form.Label>Select Season</Form.Label>
          {!hasSeasonSeasons && defaultSeason ? (
            // Show read-only display for DEFAULT season when no SEASON seasons exist
            <Form.Control
              type="text"
              value={defaultSeason.seasonLabel || defaultSeason.seasonYearRange || 'Default Season'}
              readOnly
              disabled
              style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
            />
          ) : (
            <Select
              isLoading={seasonsLoading}
              options={seasonOptions}
              value={
                button.tournamentSeason
                  ? seasonOptions.find(opt => opt.value === button.tournamentSeason) ||
                    (button.seasonName
                      ? { value: button.tournamentSeason, label: button.seasonName }
                      : null)
                  : null
              }
              onChange={(option: any) => {
                const selectedOption = seasonOptions.find(opt => opt.value === option?.value);
                onChange({ 
                  ...button, 
                  tournamentSeason: option?.value, 
                  seasonName: selectedOption?.seasonType === 'SEASON' ? option?.label : undefined
                });
              }}
              placeholder={seasonsLoading ? 'Loading seasons...' : 'Select season'}
              isClearable={hasSeasonSeasons}
              isDisabled={!hasSeasonSeasons && !!defaultSeason}
            />
          )}
          {!hasSeasonSeasons && defaultSeason && (
            <Form.Text className="text-muted">
              Using default season (no additional seasons available)
            </Form.Text>
          )}
        </Form.Group>
      )}
      
      {button.redirectionType === 'TournamentTab' && (
        <Form.Group className="mb-3">
          <Form.Label>Tab Name</Form.Label>
          <Select
            options={tabNameOptions}
            value={tabNameOptions.find(opt => opt.value === button.tabName) || null}
            onChange={(option: any) => onChange({ ...button, tabName: option?.value })}
          />
        </Form.Group>
      )}

      {button.redirectionType === 'WeeklyReport' && (
        <Form.Group className="mb-3">
          <Form.Label>Select Weekly Report</Form.Label>
           <AsyncSelect
            cacheOptions
            defaultOptions
            loadOptions={async (inputValue: string) => {
              const options = await loadWeeklyReports(inputValue);
              setWeeklyReportOptions(options);
              return options;
            }}
            value={
              button.weeklyReportId
                ? weeklyReportOptions.find(opt => opt.value === button.weeklyReportId) ||
                  (button.weeklyReportName
                    ? { value: button.weeklyReportId, label: button.weeklyReportName }
                    : null)
                : null
            }
            onChange={(option: any) => onChange({ ...button, weeklyReportId: option.value, weeklyReportName: option.label })}
          />
        </Form.Group>
      )}
    </>
  );
};

const ShareButtonForm: React.FC<{ button: ShareButton; onChange: (button: ShareButton) => void; }> = ({ button, onChange }) => {
  return (
    <Form.Group className="mb-3">
      <Form.Label>Description</Form.Label>
      <Form.Control
        as="textarea"
        rows={3}
        value={button.desc || ''}
        onChange={(e) => onChange({ ...button, desc: e.target.value })}
      />
    </Form.Group>
  );
};

export const ExtraButtonsForm: React.FC<ExtraButtonsFormProps> = ({ buttons, onChange, platform }) => {
  const handleButtonChange = (index: number, updatedButton: ExtraButton) => {
    const newButtons = [...buttons];
    newButtons[index] = updatedButton;
    onChange(newButtons);
  };

  const handleAddButton = () => {
    if (buttons.length >= 2) return;

    const hasShare = buttons.some(b => b.category === 'share');

    const newButton = hasShare ? defaultActionButton : defaultShareButton;
    
    onChange([...buttons, newButton]);
  };

  const handleRemoveButton = (index: number) => {
    onChange(buttons.filter((_, i) => i !== index));
  };

  return (
    <div>
      <h5>Extra Buttons</h5>
      <ListGroup>
        {buttons.map((button, index) => (
          <ListGroup.Item key={index}>
            <Row>
              <Col md={11}>
                <Form.Group className="mb-3">
                  <Form.Label>Category</Form.Label>
                  <Select
                    options={categoryOptions}
                    value={categoryOptions.find(opt => opt.value === button.category)}
                    isDisabled={true}
                  />
                </Form.Group>

                <Row>
                  <Col>
                    <Form.Group className="mb-3">
                      <Form.Label>Label</Form.Label>
                      <Form.Control type="text" value={button.label} onChange={e => handleButtonChange(index, {...button, label: e.target.value})} />
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group className="mb-3">
                      <Form.Label>Icon</Form.Label>
                      <Form.Control type="text" value={button.icon} onChange={e => handleButtonChange(index, {...button, icon: e.target.value})} />
                    </Form.Group>
                  </Col>
                  <Col>
                     <Form.Group className="mb-3">
                        <Form.Label>Icon Position</Form.Label>
                        <Select options={iconPositionOptions} value={iconPositionOptions.find(o => o.value === button.iconPosition)} onChange={opt => handleButtonChange(index, {...button, iconPosition: opt?.value as 'left' | 'right'})} />
                     </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                    <Col>
                        <Form.Group className="mb-3">
                            <Form.Label>Color</Form.Label>
                            <ColorPickerInput color={button.color} onChange={color => handleButtonChange(index, {...button, color})} />
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Group className="mb-3">
                            <Form.Label>Text Color</Form.Label>
                            <ColorPickerInput color={button.textColor} onChange={textColor => handleButtonChange(index, {...button, textColor})} />
                        </Form.Group>
                    </Col>
                </Row>

                {button.category === 'button' ? (
                  <ActionButtonForm button={button as ActionButton} onChange={updated => handleButtonChange(index, updated)} platform={platform} />
                ) : (
                  <ShareButtonForm button={button as ShareButton} onChange={updated => handleButtonChange(index, updated)} />
                )}
              </Col>
              <Col md={1} className="d-flex align-items-center">
                <Button variant="danger" onClick={() => handleRemoveButton(index)}>X</Button>
              </Col>
            </Row>
          </ListGroup.Item>
        ))}
      </ListGroup>
      <Button onClick={handleAddButton} className="mt-2" disabled={buttons.length >= 2}>
        Add Button
      </Button>
    </div>
  );
};

export function areExtraButtonsValid(buttons: ExtraButton[]): boolean {
  return buttons.every(btn => {
    if (btn.category === 'button') {
      if (btn.redirectionType === 'URL') {
        return !!btn.url && btn.url.trim().length > 0;
      }
      if (btn.redirectionType === 'Page') {
        return !!btn.page && btn.page.trim().length > 0;
      }
      if (btn.redirectionType === 'Custom') {
        return (
          !!btn.customType &&
          btn.customType.trim().length > 0 &&
          !!btn.customPath &&
          btn.customPath.trim().length > 0
        );
      }
      if (btn.redirectionType === 'AcademyDetailsScreen') {
        return !!btn.academyId && btn.academyId.trim().length > 0;
      }
      if (btn.redirectionType === 'TournamentScreen') {
        return !!btn.tournamentId && btn.tournamentId.trim().length > 0 && !!btn.tournamentSeason && btn.tournamentSeason.trim().length > 0;
      }
      if (btn.redirectionType === 'TournamentTab') {
        return !!btn.tournamentId && btn.tournamentId.trim().length > 0 && !!btn.tabName && btn.tabName.trim().length > 0 && !!btn.tournamentSeason && btn.tournamentSeason.trim().length > 0;
      }
      if (btn.redirectionType === 'WeeklyReport') {
        return !!btn.weeklyReportId && btn.weeklyReportId.trim().length > 0;
      }
    }
    return true;
  });
} 